import { array, bool, number, string } from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import styled from "styled-components/macro";

import { usePrefetchGetAvailableBoards } from "Boards/APIReactQuery/queries";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import {
  FnrPopover,
  FnrPopoverOption,
  FnrPopoverOptionIcon,
  FnrPopoverOptionsList,
  useKeepOpenList,
} from "UI/FnrPopover2";

import API from "../API";
import { BoardContext } from "../BoardContext";
import { TitleCellWrapper } from "../Cells";
import {
  getNextColumnOrderBy,
  getPrevColumnOrderBy,
} from "../Utils/getColumnOrderBy";
import { COLUMN_TYPES, isExtendedFieldColumn, isSignupColumn } from "../const";
import useGroupOrSignupID from "../useGroupOrSignupID";

import ShareWithAnotherBoardPopover from "./ShareWithAnotherBoardPopover";

ColumnMoreMenu.propTypes = {
  columnID: number,
  columnTitle: string,
  columnType: number,
  columnIndex: number,
  isShared: bool,
  canEdit: bool,
  canFilter: bool,
  canDelete: bool,
  relatedSignups: array,
};

export default function ColumnMoreMenu({
  columnID,
  columnTitle,
  columnType,
  columnIndex,
  isShared,
  canEdit,
  canFilter,
  canDelete,
  relatedSignups,
}) {
  const { signupTab } = useParams();
  const { pathname } = useLocation();

  const [{ boardID, columns, filters, groupID }, dispatch] =
    useContext(BoardContext);

  const buttonRef = useRef();
  const deleteButtonRef = useRef();
  const checkAllButtonRef = useRef();
  const unCheckAllButtonRef = useRef();
  const relatedSignupsButtonRef = useRef();

  const { reasonsToKeepOpen, addReason, removeReason } = useKeepOpenList();

  const groupOrSignupID = useGroupOrSignupID();

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showCheckAllConfirmation, setShowCheckAllConfirmation] =
    useState(false);
  const [showUnCheckAllConfirmation, setShowUnCheckAllConfirmation] =
    useState(false);
  const [
    showShareWithAnotherBoardPopover,
    setShowShareWithAnotherBoardPopover,
  ] = useState(false);
  const [showRelatedSignups, setShowRelatedSignups] = useState(false);

  const { prefetch: prefetchAvailableBoards } = usePrefetchGetAvailableBoards({
    columnID,
    currentBoardID: boardID,
  });

  const onShouldCloseMoreMenu = () => {
    if (reasonsToKeepOpen.current.length === 0) setShowMoreMenu(false);
  };

  const onShouldCloseDeleteMenu = () => {
    setShowDeleteConfirmation(false);
    removeReason("delete_column");
  };

  const onShouldCloseCheckAllMenu = () => {
    setShowCheckAllConfirmation(false);
    removeReason("check_all");
  };

  const onShouldCloseUnCheckAllMenu = () => {
    setShowUnCheckAllConfirmation(false);
    removeReason("uncheck_all");
  };

  const moveColumnLeft = () => {
    // gets the orderBy value of the column to the left
    const prevColOrderBy = getPrevColumnOrderBy(columnID, columns);
    moveColumn(prevColOrderBy);
  };

  const moveColumnRight = () => {
    // gets the orderBy value of the column to the right
    const nextColOrderBy = getNextColumnOrderBy(columnID, columns);
    moveColumn(nextColOrderBy);
  };

  const moveColumn = (newOrderBy) => {
    API.reorderColumn(boardID, {
      columnID,
      newOrder: newOrderBy,
      ...groupOrSignupID,
    })
      .post()
      .then((data) => {
        dispatch({ type: "Update Columns", payload: data.columns });
      });

    onShouldCloseMoreMenu();
  };

  const deleteColumn = () => {
    const filters = window.fnReactBus.getCurrentFilterRequest();
    API.deleteColumn(boardID, {
      columnID,
      request: filters,
      ...groupOrSignupID,
    })
      .post()
      .then((res) => {
        if (res.rowTotals) {
          dispatch({ type: "Update rowTotals", payload: res.rowTotals });
        }
        if (res.rows) {
          dispatch({ type: "Update Rows", payload: res.rows });
        }

        window.fnReactBus.sheetColumnDeleted(columnID);
      });

    toggleDeleteConfirmation();

    setTimeout(() => onShouldCloseMoreMenu(), 150);

    setTimeout(() => {
      dispatch({
        type: "Update Columns",
        payload: columns.filter((col) => col.ID !== columnID),
      });
    }, 500);
  };

  const toggleDeleteConfirmation = () => {
    setShowDeleteConfirmation((currentVal) => {
      if (currentVal) {
        removeReason("delete_column");
        return false;
      } else {
        addReason("delete_column");
        return true;
      }
    });
  };

  const toggleCheckAllConfirmation = () => {
    setShowCheckAllConfirmation((currentVal) => {
      if (currentVal) {
        removeReason("check_all");
        return false;
      } else {
        addReason("check_all");
        return true;
      }
    });
  };

  const toggleUnCheckAllConfirmation = () => {
    setShowUnCheckAllConfirmation((currentVal) => {
      if (currentVal) {
        removeReason("uncheck_all");
        return false;
      } else {
        addReason("uncheck_all");
        return true;
      }
    });
  };

  const addToFiltersPanel = () => {
    let filterObj = false;
    const colTitle = columns.find((col) => col.ID === columnID)?.Header;

    if (colTitle || colTitle === "") {
      for (const key in filters.filters) {
        if (filters.filters[key].isSheet) {
          if (!filterObj && filters.filters[key].categories[colTitle])
            filterObj = filters.filters[key].categories[colTitle];
        }
      }
    }

    if (filterObj) {
      window.fnReactBus.filtersPanel.addNewFilter(colTitle, filterObj);
      onShouldCloseMoreMenu();
    } else alert("Hmmm... I can't find that filter.  Try reloading the page.");
  };

  const checkAll = () => {
    const filters = window.fnReactBus.getCurrentFilterRequest();

    API.checkAll(boardID, { columnID, request: filters, ...groupOrSignupID })
      .post()
      .then((res) => {
        if (res.rows) dispatch({ type: "Update Rows", payload: res.rows });
        if (res.rowTotals)
          dispatch({ type: "Update rowTotals", payload: res.rowTotals });
        onShouldCloseMoreMenu();
      });

    toggleCheckAllConfirmation();
    setTimeout(() => onShouldCloseMoreMenu(), 150);
  };

  const uncheckAll = () => {
    const filters = window.fnReactBus.getCurrentFilterRequest();

    API.uncheckAll(boardID, { columnID, request: filters, ...groupOrSignupID })
      .post()
      .then((res) => {
        if (res.rows) dispatch({ type: "Update Rows", payload: res.rows });
        if (res.rowTotals)
          dispatch({ type: "Update rowTotals", payload: res.rowTotals });
        onShouldCloseMoreMenu();
      });

    toggleUnCheckAllConfirmation();
    setTimeout(() => onShouldCloseMoreMenu(), 150);
  };

  const shouldDisableDelete = () => {
    // don't let them delete the last column, because then there's no way to add new columns
    // (note, this checks for 2 here because of the names column)
    return columns?.length === 2;
  };

  const columnIsFromSignup = isSignupColumn(columnType);
  const isSignupReport =
    pathname.includes("signups") && signupTab === "respondents";

  const columnIsExtendedField = isExtendedFieldColumn(columnType);

  let deleteSubText = "This cannot be undone and all data will be lost.";
  if (columnIsExtendedField) {
    deleteSubText =
      "This extended field column will be removed from this report, but no data will be deleted.";
  } else if (columnIsFromSignup) {
    deleteSubText =
      "This signup column will be removed from this report, but no data will be deleted.";
  } else if (isShared) {
    deleteSubText =
      "This column will be removed from this report.  Since it is a shared column, it will still be available in other reports it is shared with.";
  }

  return (
    <>
      <MoreButton
        ref={buttonRef}
        data-cy="boardColumnMoreMenuButton"
        onClick={() => setShowMoreMenu((current) => !current)}
        className={showMoreMenu ? "show_more_menu" : null}
      >
        <div className="fas fa-bars" />
      </MoreButton>
      <FnrPopoverOptionsList
        show={showMoreMenu}
        onShouldClose={onShouldCloseMoreMenu}
        buttonRef={buttonRef}
        style={{ width: 260 }}
      >
        {isShared && (
          <IsSharedDisclaimer>
            <IsSharedDisclaimerHeader>
              <IsSharedDisclaimerIcon className="fal fa-share" />
              <span>Shared Column</span>
            </IsSharedDisclaimerHeader>
            <IsSharedDisclaimerText>
              This column is shared with other reports. Making changes to any
              data in it will change it in other reports. And changing data in
              it in other reports will change it here.
            </IsSharedDisclaimerText>
          </IsSharedDisclaimer>
        )}
        {canFilter && (
          <>
            <FnrPopoverOption
              data-cy="boardColumnMoreMenuFilterButton"
              onClick={addToFiltersPanel}
            >
              <FnrPopoverOptionIcon icon="filter fas" />
              Filter
            </FnrPopoverOption>
            <Spacer />
          </>
        )}
        {columnType === 1 && canEdit && (
          <>
            <FnrPopoverOption
              ref={checkAllButtonRef}
              data-cy="boardColumnMoreMenuCheckAllButton"
              onClick={toggleCheckAllConfirmation}
            >
              <FnrPopoverOptionIcon icon="tasks fas" />
              Check All
            </FnrPopoverOption>
            <FnrPopoverOption
              ref={unCheckAllButtonRef}
              data-cy="boardColumnMoreMenuUncheckAllButton"
              onClick={toggleUnCheckAllConfirmation}
            >
              <FnrPopoverOptionIcon icon="list-ul far" />
              Uncheck All
            </FnrPopoverOption>
            <Spacer />
          </>
        )}
        {columnIndex > 1 && (
          <FnrPopoverOption
            data-cy="boardColumnMoreMenuMoveColumnLeftButton"
            onClick={moveColumnLeft}
          >
            <FnrPopoverOptionIcon icon="arrow-left fad" />
            Move Column Left
          </FnrPopoverOption>
        )}
        {columnIndex < columns.length - 1 && (
          <FnrPopoverOption
            data-cy="boardColumnMoreMenuMoveColumnRightButton"
            onClick={moveColumnRight}
          >
            <FnrPopoverOptionIcon icon="arrow-right fad" />
            Move Column Right
          </FnrPopoverOption>
        )}

        {(isSignupReport || !columnIsFromSignup) && (
          <FnrPopoverOption
            data-cy="boardColumnShareWithAnotherBoard"
            onClick={() => {
              setShowMoreMenu(false);
              setShowShareWithAnotherBoardPopover(true);
            }}
            onMouseEnter={prefetchAvailableBoards}
            onFocus={prefetchAvailableBoards}
          >
            <FnrPopoverOptionIcon icon="share fad" />
            Share with Another Report
          </FnrPopoverOption>
        )}

        {relatedSignups?.length > 0 && (
          <FnrPopoverOption
            ref={relatedSignupsButtonRef}
            data-cy="boardColumnRelatedSignups"
            onClick={() => {
              setShowRelatedSignups((prev) => !prev);
            }}
          >
            <FnrPopoverOptionIcon icon="clipboard-check fad" />
            Related Signup{isSignupReport && relatedSignups?.length > 1 && "s"}
          </FnrPopoverOption>
        )}

        {canDelete && (
          <>
            <Spacer />
            <FnrPopoverOption
              ref={deleteButtonRef}
              forceHover={showDeleteConfirmation}
              data-cy="boardColumnMoreMenuDeleteColumnButton"
              onClick={toggleDeleteConfirmation}
              disabled={shouldDisableDelete()}
            >
              <FnrPopoverOptionIcon icon="trash-alt fas" />
              Delete Column
            </FnrPopoverOption>
          </>
        )}
        {/* Check-All Confirmation */}
        <FnrPopover
          show={showCheckAllConfirmation}
          buttonRef={checkAllButtonRef}
          onShouldClose={onShouldCloseCheckAllMenu}
        >
          <Confirmation>
            Are you sure you want to check all checkboxes in this column?
            <DeleteSubtext>This cannot be undone.</DeleteSubtext>
            <ButtonRow>
              <FnrButton
                data-cy="cancelcheckAll"
                onClick={toggleCheckAllConfirmation}
              >
                Cancel
              </FnrButton>
              <FnrButton color="blue" data-cy="checkAll" onClick={checkAll}>
                Check &apos;Em!&nbsp;
              </FnrButton>
            </ButtonRow>
          </Confirmation>
        </FnrPopover>

        {/* Uncheck-All Confirmation */}
        <FnrPopover
          show={showUnCheckAllConfirmation}
          buttonRef={unCheckAllButtonRef}
          onShouldClose={onShouldCloseUnCheckAllMenu}
        >
          <Confirmation>
            Are you sure you want to un-check all checkboxes in this column?
            <DeleteSubtext>This cannot be undone.</DeleteSubtext>
            <ButtonRow>
              <FnrButton
                data-cy="cancelUncheckAll"
                onClick={toggleUnCheckAllConfirmation}
              >
                Cancel
              </FnrButton>
              <FnrButton color="blue" data-cy="uncheckAll" onClick={uncheckAll}>
                Uncheck &apos;Em!&nbsp;
              </FnrButton>
            </ButtonRow>
          </Confirmation>
        </FnrPopover>

        {/* Delete Confirmation */}
        <FnrPopover
          style={{
            backgroundImage:
              "linear-gradient( to right bottom, rgba(180, 37, 45, 0.7), rgba(128, 36, 40, 0.7) )",
          }}
          show={showDeleteConfirmation}
          buttonRef={deleteButtonRef}
          onShouldClose={onShouldCloseDeleteMenu}
        >
          <Confirmation>
            Are you sure you want to delete this column?
            <DeleteSubtext>{deleteSubText}</DeleteSubtext>
            <ButtonRow>
              <FnrButton
                data-cy="cancelDeleteColumnConfirmation"
                onClick={toggleDeleteConfirmation}
              >
                Cancel
              </FnrButton>
              <FnrButton
                color="red"
                data-cy="deleteColumnConfirmationAdios"
                onClick={deleteColumn}
              >
                <ButtonText>Adios</ButtonText>
                <ButtonIcon fas fa-hand-peace />
              </FnrButton>
            </ButtonRow>
          </Confirmation>
        </FnrPopover>

        {/* Related Signups Popover */}
        {relatedSignups?.length > 0 && (
          <FnrPopover
            show={showRelatedSignups}
            buttonRef={relatedSignupsButtonRef}
          >
            <RelatedSignupList>
              {relatedSignups?.map((signup) => (
                <li key={signup.ID}>
                  <button
                    onClick={() =>
                      window.fnReactBus.goToUrl(
                        `/signups/${signup.ID}/respondents`
                      )
                    }
                  >
                    {signup.name}
                  </button>
                </li>
              ))}
            </RelatedSignupList>
          </FnrPopover>
        )}
      </FnrPopoverOptionsList>

      {/* Share with another board popover */}
      <ShareWithAnotherBoardPopover
        buttonRef={buttonRef}
        show={showShareWithAnotherBoardPopover}
        onShouldClose={() => {
          setShowShareWithAnotherBoardPopover(false);
        }}
        columnID={columnID}
        columnTitle={columnTitle}
        currentBoardID={boardID}
      />
    </>
  );
}

const Spacer = styled.div`
  height: 12px;
`;

const IsSharedDisclaimer = styled.div`
  padding: 7px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  margin-bottom: 7px;
`;
const IsSharedDisclaimerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  margin-bottom: 6px;
`;
const IsSharedDisclaimerIcon = styled.div``;
const IsSharedDisclaimerText = styled.div`
  font-style: italic;
  font-size: 14px;
`;

const MoreButton = styled.div`
  position: absolute;
  right: 12px;
  font-size: 12px;
  width: 24px;
  height: 24px;
  color: #828c8d;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
  padding-top: 1px;
  background: #eceff0;
  box-shadow: #eceff0 0 0 6px;
  opacity: 0;
  visibility: hidden;
  border: 2px solid #eceff0;
  transition: opacity 0.2s, visibility 0.2s, background 0.2s, color 0.2s,
    transform 0.2s;

  ${TitleCellWrapper}:hover:not(:focus-within) &,
  &.show_more_menu {
    opacity: 1;
    visibility: visible;
  }

  &:hover,
  &.show_more_menu {
    background: #278dc1;
    color: #eceff0;
  }

  &:not(.show_more_menu):active {
    transform: scale(0.85);
  }
`;

const Confirmation = styled.div`
  font-size: 20px;
  padding: 8px;
  max-width: 260px;
  box-sizing: border-box;
`;

const DeleteSubtext = styled.div`
  font-size: 14px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
`;

const RelatedSignupList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 8px;

  & li button {
    border: none;
    box-shadow: none;
    background: transparent;
    color: white;
    cursor: pointer;
    font-size: 16px;
    font-weight: 400;
    width: 100%;
    text-align: left;
    padding-block: 2px;
    transition: scale 0.2s ease;

    &:hover {
      scale: 1.05;
    }
  }
`;
